import {
  activityEvents as mockActivityEvents,
  audioAssets as mockAudioAssets,
  contentItems as mockContentItems,
  pdfAssets as mockPdfAssets,
} from "@/data/mockData";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { deleteFileByPublicUrl } from "@/services/storageService";
import type { AccessRule, ActivityEvent, AppUser, AudioAsset, ContentItem, PdfAsset } from "@/types/content";
import type { AudioMode, ContentStatus, ContentType } from "@/types/content";

export interface ContentProcessingStatus {
  content_item_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  error_message: string | null;
  chunks_count: number;
  started_at: string | null;
  completed_at: string | null;
}

export interface UploadedAudioTrack {
  url: string;
  title: string;
  durationSeconds: number;
  sizeMb: number;
  mimeType: string;
}

export interface DashboardData {
  contentItems: ContentItem[];
  audioAssets: AudioAsset[];
  pdfAssets: PdfAsset[];
  accessRules: AccessRule[];
  users: AppUser[];
  activityEvents: ActivityEvent[];
  source: "supabase" | "mock";
}

export interface ContentListData {
  contentItems: ContentItem[];
  audioAssets: AudioAsset[];
  pdfAssets: PdfAsset[];
  accessRules: AccessRule[];
  source: "supabase" | "mock";
}

export interface SaveContentItemInput {
  id?: string;
  slug?: string;
  title: string;
  subtitle: string;
  author: string;
  category: string;
  contentType: ContentType;
  status: ContentStatus;
  isFeatured: boolean;
  descriptionShort: string;
  descriptionLong: string;
  tags: string[];
  coverImageUrl: string;
  audioMode: AudioMode;
  audioUrl: string;
  audioFileSizeMb: number;
  audioMimeType: string;
  audioTracks?: UploadedAudioTrack[];
  narrator: string;
  audioQuality: string;
  totalDurationSeconds: number;
  pdfUrl: string;
  previewPdfUrl?: string;
  pdfFileSizeMb: number;
  pdfMimeType: string;
  pdfPageCount: number;
  previewPageCount: number;
  pdfIsDownloadable: boolean;
  accessType: AccessRule["access_type"];
  price: number | null;
  storeProductId?: string | null;
}

function getMockDashboardData(): DashboardData {
  return {
    contentItems: mockContentItems,
    audioAssets: mockAudioAssets,
    pdfAssets: mockPdfAssets,
    accessRules: [],
    users: [],
    activityEvents: mockActivityEvents,
    source: "mock",
  };
}

function getMockContentListData(): ContentListData {
  return {
    contentItems: mockContentItems,
    audioAssets: mockAudioAssets,
    pdfAssets: mockPdfAssets,
    accessRules: [],
    source: "mock",
  };
}

export async function getDashboardData(): Promise<DashboardData> {
  if (!isSupabaseConfigured || !supabase) {
    return getMockDashboardData();
  }

  try {
    const [contentRes, audioRes, pdfRes, accessRes, usersRes, activityRes] = await Promise.all([
      supabase.from("content_items").select("*").order("updated_at", { ascending: false }),
      supabase.from("audio_assets").select("*"),
      supabase.from("pdf_assets").select("*"),
      supabase.from("access_rules").select("*"),
      supabase.from("app_users").select("*"),
      supabase.from("activity_events").select("*").order("timestamp", { ascending: false }).limit(20),
    ]);

    if (contentRes.error || audioRes.error || pdfRes.error || accessRes.error || usersRes.error || activityRes.error) {
      throw new Error(
        [
          contentRes.error?.message,
          audioRes.error?.message,
          pdfRes.error?.message,
          accessRes.error?.message,
          usersRes.error?.message,
          activityRes.error?.message,
        ]
          .filter(Boolean)
          .join(" | "),
      );
    }

    return {
      contentItems: (contentRes.data as ContentItem[]) || [],
      audioAssets: (audioRes.data as AudioAsset[]) || [],
      pdfAssets: (pdfRes.data as PdfAsset[]) || [],
      accessRules: (accessRes.data as AccessRule[]) || [],
      users: (usersRes.data as AppUser[]) || [],
      activityEvents: (activityRes.data as ActivityEvent[]) || [],
      source: "supabase",
    };
  } catch (error) {
    console.warn("Falling back to mock dashboard data:", error);
    return getMockDashboardData();
  }
}

export async function getContentListData(): Promise<ContentListData> {
  if (!isSupabaseConfigured || !supabase) {
    return getMockContentListData();
  }

  try {
    const [contentRes, audioRes, pdfRes, accessRes] = await Promise.all([
      supabase.from("content_items").select("*").order("updated_at", { ascending: false }),
      supabase.from("audio_assets").select("*"),
      supabase.from("pdf_assets").select("*"),
      supabase.from("access_rules").select("*"),
    ]);

    if (contentRes.error || audioRes.error || pdfRes.error || accessRes.error) {
      throw new Error(
        [contentRes.error?.message, audioRes.error?.message, pdfRes.error?.message, accessRes.error?.message].filter(Boolean).join(" | "),
      );
    }

    return {
      contentItems: (contentRes.data as ContentItem[]) || [],
      audioAssets: (audioRes.data as AudioAsset[]) || [],
      pdfAssets: (pdfRes.data as PdfAsset[]) || [],
      accessRules: (accessRes.data as AccessRule[]) || [],
      source: "supabase",
    };
  } catch (error) {
    console.warn("Falling back to mock content list data:", error);
    return getMockContentListData();
  }
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export async function saveContentItem(payload: SaveContentItemInput): Promise<{ id: string; source: "supabase" | "mock" }> {
  if (!isSupabaseConfigured || !supabase) {
    return { id: payload.id || crypto.randomUUID().slice(0, 8), source: "mock" };
  }

  const now = new Date().toISOString();
  const contentId = payload.id || crypto.randomUUID().slice(0, 8);
  const slug = payload.slug || slugify(payload.title);

  const contentRecord = {
    id: contentId,
    slug,
    title: payload.title,
    subtitle: payload.subtitle || "",
    author: payload.author || "",
    category: payload.category || "",
    content_type: payload.contentType,
    status: payload.status,
    is_featured: payload.isFeatured,
    description_short: payload.descriptionShort || "",
    description_long: payload.descriptionLong || "",
    tags: payload.tags || [],
    language: "es",
    cover_image_url: payload.coverImageUrl || "",
    published_at: payload.status === "published" ? now : null,
    updated_at: now,
  };

  const { error: contentError } = await supabase.from("content_items").upsert(contentRecord);
  if (contentError) {
    throw contentError;
  }

  if (payload.contentType === "audio" || payload.contentType === "mixed") {
    const tracks = payload.audioTracks || [];
    const resolvedMode: AudioMode = tracks.length > 1 ? "chapters" : "single_file";
    const resolvedAudioUrl = tracks[0]?.url || payload.audioUrl || "";
    const resolvedTotalDuration =
      tracks.length > 0
        ? tracks.reduce((sum, track) => sum + (track.durationSeconds || 0), 0)
        : payload.totalDurationSeconds || 0;
    const resolvedFileSizeMb =
      tracks.length > 0
        ? Number(tracks.reduce((sum, track) => sum + (track.sizeMb || 0), 0).toFixed(2))
        : payload.audioFileSizeMb || 0;
    const resolvedMimeType = tracks[0]?.mimeType || payload.audioMimeType || "";

    const { error: audioError } = await supabase.from("audio_assets").upsert({
      content_item_id: contentId,
      audio_mode: resolvedMode,
      audio_url: resolvedAudioUrl,
      total_duration_seconds: resolvedTotalDuration,
      file_size_mb: resolvedFileSizeMb,
      mime_type: resolvedMimeType,
      narrator: payload.narrator || "",
      audio_quality: payload.audioQuality || "320kbps",
      has_preview: false,
      preview_seconds: 0,
    });
    if (audioError) throw audioError;

    if (tracks.length > 1) {
      const { error: deleteChaptersError } = await supabase.from("audio_chapters").delete().eq("content_item_id", contentId);
      if (deleteChaptersError) throw deleteChaptersError;

      const chapterRows = tracks.map((track, index) => ({
        id: crypto.randomUUID().slice(0, 8),
        content_item_id: contentId,
        chapter_number: index + 1,
        chapter_title: track.title || `Capítulo ${index + 1}`,
        audio_url: track.url,
        duration_seconds: track.durationSeconds || 0,
        sort_order: index + 1,
        is_published: true,
      }));
      const { error: insertChaptersError } = await supabase.from("audio_chapters").insert(chapterRows);
      if (insertChaptersError) throw insertChaptersError;
    }
  }

  if (payload.contentType === "pdf" || payload.contentType === "mixed") {
    const { error: pdfError } = await supabase.from("pdf_assets").upsert({
      content_item_id: contentId,
      pdf_url: payload.pdfUrl || "",
      preview_pdf_url: payload.previewPdfUrl || null,
      file_size_mb: payload.pdfFileSizeMb || 0,
      mime_type: payload.pdfMimeType || "",
      page_count: payload.pdfPageCount || 0,
      preview_page_count: payload.previewPageCount || 10,
      pdf_mode: "full_book_plain_pdf",
      is_downloadable: payload.pdfIsDownloadable,
    });
    if (pdfError) throw pdfError;
  }

  const { error: accessError } = await supabase.from("access_rules").upsert({
    content_item_id: contentId,
    access_type: payload.accessType || "free",
    plan_id: payload.accessType === "subscription" ? "plan-premium" : null,
    price: payload.accessType === "single_purchase" ? payload.price ?? null : null,
    store_product_id:
      payload.accessType === "single_purchase" ? payload.storeProductId ?? null : null,
  });
  if (accessError) throw accessError;

  return { id: contentId, source: "supabase" };
}

export async function deleteContentItem(contentId: string): Promise<{ source: "supabase" | "mock"; warnings: string[] }> {
  if (!isSupabaseConfigured || !supabase) {
    return { source: "mock", warnings: [] };
  }

  const warnings: string[] = [];

  const [contentRes, audioRes, pdfRes, chaptersRes] = await Promise.all([
    supabase.from("content_items").select("id,cover_image_url").eq("id", contentId).maybeSingle(),
    supabase.from("audio_assets").select("audio_url").eq("content_item_id", contentId).maybeSingle(),
    supabase.from("pdf_assets").select("pdf_url").eq("content_item_id", contentId).maybeSingle(),
    supabase.from("audio_chapters").select("audio_url").eq("content_item_id", contentId),
  ]);

  if (contentRes.error) throw contentRes.error;
  if (audioRes.error) throw audioRes.error;
  if (pdfRes.error) throw pdfRes.error;
  if (chaptersRes.error) throw chaptersRes.error;

  const deleteIfPresent = async (bucket: "content-audio" | "content-docs" | "content-covers", url?: string | null) => {
    if (!url) return;
    try {
      await deleteFileByPublicUrl({ bucket, publicUrl: url });
    } catch (error) {
      const message = error instanceof Error ? error.message : "error desconocido";
      warnings.push(`No se pudo limpiar archivo en ${bucket}: ${message}`);
    }
  };

  await deleteIfPresent("content-covers", contentRes.data?.cover_image_url || null);
  await deleteIfPresent("content-audio", audioRes.data?.audio_url || null);
  await deleteIfPresent("content-docs", pdfRes.data?.pdf_url || null);

  for (const chapter of chaptersRes.data || []) {
    await deleteIfPresent("content-audio", chapter.audio_url);
  }

  const { error: deleteError } = await supabase.from("content_items").delete().eq("id", contentId);
  if (deleteError) throw deleteError;

  return { source: "supabase", warnings };
}

// --- Content Processing (RAG) ---

export async function getProcessingStatus(
  contentItemId: string
): Promise<ContentProcessingStatus | null> {
  if (!isSupabaseConfigured || !supabase) return null;
  const { data } = await supabase
    .from("content_processing")
    .select("*")
    .eq("content_item_id", contentItemId)
    .maybeSingle();
  return data || null;
}

export async function getProcessingStatuses(
  contentItemIds: string[]
): Promise<Record<string, ContentProcessingStatus>> {
  if (!isSupabaseConfigured || !supabase || !contentItemIds.length) return {};
  const { data } = await supabase
    .from("content_processing")
    .select("*")
    .in("content_item_id", contentItemIds);
  if (!data) return {};
  return Object.fromEntries(
    data.map((row: ContentProcessingStatus) => [row.content_item_id, row])
  );
}

export async function triggerContentProcessing(
  contentItemId: string
): Promise<{ success: boolean; chunks_count?: number }> {
  if (!isSupabaseConfigured || !supabase) throw new Error("Supabase no configurado");

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("No autenticado");

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const res = await fetch(`${supabaseUrl}/functions/v1/process-content`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content_item_id: contentItemId }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Error al procesar el contenido");
  return json;
}
